import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import { Routes, Route, Link, Navigate, useNavigate } from "react-router-dom";
import "../App.css";
import { IconContext } from "react-icons";
import logo from "../assets/images/webbLogo1.png";
import { SidebarData, SidebarItem } from "./SidebarData";
import SubMenu from "./SubMenu";
import { forgotPasswordUser } from "../slices/auth";
import { State } from "../interfaces/types";
import { AppDispatch } from "../store";
import { useAppDispatch } from "../hooks";
import ProtectedRoute from "./ProtectedRoute";
import { Alert, Button } from "react-bootstrap";
const Login = lazy(() => import("./Login"));
const ForgotPassword = lazy(() => import("./ForgotPassword"));
const ResetPassword = lazy(() => import("./ResetPassword"));
const ResetPasswordClient = lazy(() => import("./ResetPasswordClient"));
const Users = lazy(() => import("./Users"));
const Home = lazy(() => import("./Home"));
const AdminHome = lazy(() => import("./AdminHome"));
const UnauthorizedPage = lazy(() => import("./UnauthorizedPage"));
const Profile = lazy(() => import("./Profile"));
const Payments = lazy(() => import("./Payments"));
const Configurations = lazy(() => import("./Configurations"));
const Reports = lazy(() => import("./Reports"));
const Coaches = lazy(() => import("./Coaches"));
const Clients = lazy(() => import("./Clients"));
const ClientRegistry = lazy(() => import("./ClientRegistry"));
const PaymentRegistry = lazy(() => import("./PaymentRegistry"));
const MyTrainings = lazy(() => import("./MyTrainings"));
const MyClasses = lazy(() => import("./MyClasses"));
const Classes = lazy(() => import("./Classes"));
const ClassesInstructor = lazy(() => import("./ClassesInstructor"));
const Routines = lazy(() => import("./Routines"));
const Exercises = lazy(() => import("./Exercises"));
const Agreements = lazy(() => import("./Agreements"));
const AgreementAdmin = lazy(() => import("./AgreementAdmin"));
const MyPayments = lazy(() => import("./MyPayments"));
const MyRoutines = lazy(() => import("./MyRoutines"));
const Plans = lazy(() => import("./Plans"));
const ClientPlansPage = lazy(() => import("./ClientPlansPage"));
const TermsAndConditions = lazy(() => import("./TermsAndConditions"));
const UnderConstruction = lazy(() => import("./UnderConstruction"));

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useSelector((state: State) => state.auth);

  const [sidebarVisible, setSidebarVisible] = useState<boolean>(false);
  const dispatch: AppDispatch = useAppDispatch();
  const hasRunRef = useRef<string | null>(null);
  const [firstLoginError, setFirstLoginError] = useState("");
  const [retry, setRetry] = useState(0);

  const isFirstLogin = currentUser?.firstLogin;

  // ✅ Solo clientes traen conditions. Si no existe, lo tratamos como aceptado.
  const mustAcceptConditions =
    !!currentUser &&
    Object.prototype.hasOwnProperty.call(currentUser, "conditions") &&
    currentUser.conditions === false;

  // ✅ 1) Si cliente NO aceptó términos => mandarlo a /terms-conditions
  // ✅ 2) Si ya aceptó y es primer login => continuar con reset password
  useEffect(() => {
    if (!currentUser) { hasRunRef.current = null; return; }

    // 1) Terms & Conditions primero
    if (mustAcceptConditions) {
      navigate("/terms-conditions", { replace: true });
      return;
    }

    // 2) Redirección automática al reset de contraseña solo en primer login
    if (hasRunRef.current === currentUser.username || !isFirstLogin || !currentUser?.username) return;

    hasRunRef.current = currentUser.username;
    setFirstLoginError("");

    const { username } = currentUser;

    dispatch(forgotPasswordUser({ username }))
      .unwrap()
      .then((response) => {
        const token = response.token;

        const isClient = currentUser.roles.includes("ROLE_CLIENT");
        navigate(isClient ? `/reset-password-client/${token}` : `/reset-password/${token}`, {
          replace: true,
        });
      })
      .catch(() => {
        hasRunRef.current = null;
        setFirstLoginError("No se pudo preparar el cambio de contraseña. Intente nuevamente.");
      });
  }, [currentUser, mustAcceptConditions, isFirstLogin, dispatch, navigate, retry]);

  const toggleSidebar = () => {
    setSidebarVisible((prev) => !prev);
  };

  return (
    <>
      {/* ✅ Ocultamos navbar/side menu solo durante el primer login (reset) o cuando está en términos */}
      {!isFirstLogin && !mustAcceptConditions && (
        <IconContext.Provider value={{ color: "undefined" }}>
          <nav className="navbar navbar-expand-lg navbar-dark">
            <div className="container-fluid">
              <button
                className="navbar-toggler"
                type="button"
                onClick={toggleSidebar}
              >
                <span className="navbar-toggler-icon"></span>
              </button>

              {currentUser && (
                <Link to={"/home"} className="navbar-brand">
                  <img
                    src={logo}
                    alt="gimnasio-webb-logo"
                    className="logo-img-nav"
                  />
                </Link>
              )}

              <div className="collapse navbar-collapse">
                <ul className="navbar-nav mr-auto">
                  <li className="nav-item">
                    {currentUser && (
                      <Link to={"/home"} className="nav-link">
                        Sistema Gimnasio Webb
                      </Link>
                    )}
                  </li>
                </ul>
              </div>

              {currentUser && (
                <>
                  {/* Desktop */}
                  <ul className="navbar-nav ms-auto d-none d-lg-flex">
                    <li className="nav-item">
                      <Link to={"/home"} className="nav-link">
                        Bienvenido(a), {currentUser.name + " " + currentUser.lastname}
                      </Link>
                    </li>
                  </ul>

                  {/* Mobile */}
                  <div className="w-85 d-flex d-lg-none justify-content-center mt-2">
                    <span className="nav-item nav-link text-center">
                      Bienvenido(a), {currentUser.name + " " + currentUser.lastname}
                    </span>
                  </div>
                </>
              )}
            </div>
          </nav>

          {currentUser && (
            <nav className={`nav-menu ${sidebarVisible ? "active" : ""}`}>
              <ul className="nav-menu-items">
                {SidebarData.map((item: SidebarItem, index: number) => (
                  <SubMenu key={index} item={item} onClickMenuItem={toggleSidebar} />
                ))}
              </ul>
            </nav>
          )}
        </IconContext.Provider>
      )}

      {/* Contenedor principal de rutas */}
      <div className="container d-flex align-items-center justify-content-center">
        {firstLoginError && <Alert variant="danger">{firstLoginError} <Button onClick={() => setRetry(value => value + 1)}>Reintentar</Button></Alert>}
        <Suspense fallback={<div role="status">Cargando...</div>}>
        <Routes>
          <Route path="/" element={<ProtectedRoute path="/"><Home /></ProtectedRoute>} />
          <Route path="/home" element={<ProtectedRoute path="/home"><Home /></ProtectedRoute>} />
          <Route path="/admin-home" element={<ProtectedRoute path="/admin-home"><AdminHome /></ProtectedRoute>} />
          <Route path="/login" element={<Login />} />

          {/* ✅ NUEVO: Términos & Condiciones */}
          <Route path="/terms-conditions" element={<ProtectedRoute path="/terms-conditions"><TermsAndConditions /></ProtectedRoute>} />

          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/reset-password-client/:token" element={<ResetPasswordClient />} />
          <Route path="/profile" element={<ProtectedRoute path="/profile"><Profile /></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute path="/users"><Users /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute path="/clients"><Clients /></ProtectedRoute>} />
          <Route path="/payments" element={<ProtectedRoute path="/payments"><Payments /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute path="/reports"><Reports /></ProtectedRoute>} />
          <Route path="/coaches" element={<ProtectedRoute path="/coaches"><Coaches /></ProtectedRoute>} />
          <Route path="/configurations" element={<ProtectedRoute path="/configurations"><Configurations /></ProtectedRoute>} />
          <Route path="/payment-registry" element={<ProtectedRoute path="/payment-registry"><PaymentRegistry /></ProtectedRoute>} />
          <Route path="/client-registry" element={<ProtectedRoute path="/client-registry"><ClientRegistry /></ProtectedRoute>} />
          <Route path="/my-trainings" element={<ProtectedRoute path="/my-trainings"><MyTrainings /></ProtectedRoute>} />
          <Route path="/my-routines" element={<ProtectedRoute path="/my-routines"><MyRoutines /></ProtectedRoute>} />
          <Route path="/my-classes" element={<ProtectedRoute path="/my-classes"><MyClasses /></ProtectedRoute>} />
          <Route path="/instructor-classes" element={<ProtectedRoute path="/instructor-classes"><ClassesInstructor /></ProtectedRoute>} />
          <Route path="/my-payments" element={<ProtectedRoute path="/my-payments"><MyPayments /></ProtectedRoute>} />
          <Route
            path="/classes"
            element={<ProtectedRoute path="/classes"><Classes /></ProtectedRoute>}
          />
          <Route
            path="/exercises"
            element={<ProtectedRoute path="/exercises"><Exercises /></ProtectedRoute>}
          />
          <Route path="/trainings" element={<ProtectedRoute path="/trainings"><Routines /></ProtectedRoute>} />
          <Route path="/agreements" element={<ProtectedRoute path="/agreements"><Agreements /></ProtectedRoute>} />
          <Route path="/agreementadmin" element={<ProtectedRoute path="/agreementadmin"><AgreementAdmin /></ProtectedRoute>} />
          <Route path="/plans" element={<ProtectedRoute path="/plans"><Plans /></ProtectedRoute>} />
          <Route path="/client-plans" element={<ProtectedRoute path="/client-plans"><ClientPlansPage /></ProtectedRoute>} />
          <Route path="/unauthorized-page" element={<ProtectedRoute path="/unauthorized-page"><UnauthorizedPage /></ProtectedRoute>} />
          <Route path="/under-construction" element={<ProtectedRoute path="/under-construction"><UnderConstruction /></ProtectedRoute>} />

          {/* ruta por defecto */}
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
        </Suspense>
      </div>
    </>
  );
};

export default Navbar;
